import React, { useState, useEffect, ChangeEvent, FC } from 'react';
import gql from 'graphql-tag';
import styled from '@emotion/styled';
import { useQuery, useMutation } from '@apollo/client';
import { BlueButton, Box, Button } from '~Styles';
import { handyBreakpoint } from '~Styles/breakpoints';

import {
  useI18n,
  useSelector,
  useButton,
  useLogger,
  useOnboardingTracker,
  ONBOARDING_EVENT_STEP,
  useMetadata,
} from '~Hooks';
import _ from 'lodash';
import { useStorage } from '~Components/Storage';
import Loader from '~ComponentsImproved/Loader';
import Paragraph from '~ComponentsImproved/Typography/Paragraph';
import { GetPosDevices, GetPosDevicesVariables } from '~Scenes/Onboarding/api/__queries__';
import { GET_POS_DEVICES } from '~Scenes/Onboarding/api/queries';
import { BackButton, BackButtonIcon, FormatInput, HeaderBox } from '~Scenes/Onboarding/styles';
import Icon from '~ComponentsImproved/Icon';
import DevicesIcon from '~Assets/icons/devices.svg';
import Heading from '~ComponentsImproved/Typography/Heading';
import SelectableDevice from './SelectableDevice';
import {
  ClaimPosDevice,
  ClaimPosDeviceVariables,
  CreatePOSDevice,
  CreatePOSDeviceVariables,
} from './__queries__';

const CLAIM_POS_DEVICE = gql`
  mutation ClaimPosDevice($deviceId: ID!, $lastSeen: DateTime!) {
    updatePOSDevice(where: { id: $deviceId }, data: { claimed: true, lastSeen: $lastSeen }) {
      id
      name
    }
  }
`;

const CREATE_POS_DEVICE_MUTATION = gql`
  mutation CreatePOSDevice($name: String!, $venueId: ID!, $companyId: ID!, $lastSeen: DateTime!) {
    createPOSDevice(name: $name, venueId: $venueId, companyId: $companyId, lastSeen: $lastSeen) {
      id
      # Name is used for logging purposes to easier identify the device
      name
    }
  }
`;

const LoadingWrapper = styled(Box)`
  height: 492px;
  min-width: 592px;

  ${handyBreakpoint} {
    height: auto;
    min-width: 100%;
  }
`;

const CreateOrClaimBox = styled(Box)`
  flex: 1;
  padding-top: 4px;
  overflow: hidden;
  background-color: var(--colorBlueGray1200);
  color: var(--colorWhite);
  min-width: 400px;

  ${handyBreakpoint} {
    min-width: 100%;
  }
`;

const DevicesBox = styled(Box)`
  border: 1px solid var(--colorBlueGray1000);
  margin: 0 48px 4px 48px;
  overflow-y: scroll;
  flex: 1;

  & > *:not(:last-child) {
    border-bottom: 1px solid var(--colorBlueGray1000);
  }
`;

// Which view the user is in
enum VIEW {
  SELECT,
  CREATE,
  CLAIM,
}

interface Props {
  isHandy?: boolean;
  /** If we're in settings and just want to change the device */
  isChangeOfDevice?: boolean;
  onComplete: () => void;
}

/** This component is responsible for claiming a POS Device during the onboarding process. */
const ClaimDevice: FC<Props> = ({ isHandy, isChangeOfDevice, onComplete }) => {
  const { i18n } = useI18n();

  const venueId = useSelector((state) => state.venueId) as string;
  const companyId = useSelector((state) => state.company);
  const country = useSelector((state) => state.country);
  const defaultLanguage = useSelector((state) => state.defaultLanguage);

  const [, updateStorage] = useStorage();

  const logger = useLogger('claim-device');
  const onbTracker = useOnboardingTracker();
  const metadata = useMetadata();

  // If user has no available POS Device, they need to submit one with a name
  const [nameInput, setNameInput] = useState<string>('');
  const [selectedPosDeviceId, setSelectedPosDeviceId] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // If there are no unclaimed POS devices, prompt the user to create one
  const [view, setView] = useState<VIEW>(VIEW.SELECT);

  // company.country is undefined for most companie and in those cases we should assume its sweden.
  const isSwedishCompany = country === 'SE' || (!country && defaultLanguage === 'se');

  const posDevicesQuery = useQuery<GetPosDevices, GetPosDevicesVariables>(GET_POS_DEVICES, {
    variables: { venueId, companyId },
    skip: !venueId || !companyId,
    fetchPolicy: 'cache-and-network',
  });

  const [createPOSDeviceMutation] = useMutation<CreatePOSDevice, CreatePOSDeviceVariables>(
    CREATE_POS_DEVICE_MUTATION,
  );
  const [updatePosDevice] = useMutation<ClaimPosDevice, ClaimPosDeviceVariables>(CLAIM_POS_DEVICE);

  const posDevices = posDevicesQuery.data?.devices ?? [];

  useEffect(() => {
    if (posDevicesQuery.data?.devices && posDevices.length === 0) {
      setView(VIEW.CREATE);
    }
  }, [posDevices]);

  const claimPosDevice = async (params: { posDeviceId: string; posDeviceName: string }) => {
    const { posDeviceId, posDeviceName } = params;

    if (!metadata.data.organizationId) {
      logger.error('Trying to claim POS Device in onboarding stopped - No organizationId found');
      return { success: false, message: i18n('Onboarding.RegisterDevice.Error.NoOrgNumber') };
    }

    try {
      await updatePosDevice({
        variables: { deviceId: posDeviceId, lastSeen: new Date() },
      });
    } catch (err) {
      /** This can be due to already claimed, but we haven't had this before so I don't add it here now either */
      logger.error('Claiming POS Device failed: ' + JSON.stringify(err));
    }

    updateStorage({
      taxComplianceDeviceId: posDeviceId,
      taxComplianceDeviceName: posDeviceName,
      taxComplianceCorporateId: metadata.data.organizationId,
    });

    return { success: true };
  };

  // Here we create a new POS Device
  const createPosDeviceFlow = async () => {
    if (isSwedishCompany && !nameInput.match(/^[a-z0-9]+$/)) {
      // Compliance requirements in Sweden
      setError(i18n('Onboarding.RegisterDevice.Error.SwedenCompliance'));
      return;
    }

    const isFirstPosDeviceCreated = posDevices.length === 0;
    const isPosDeviceNameTaken = posDevices.find((d) => d.name === nameInput);

    if (isPosDeviceNameTaken) {
      setError(i18n('Onboarding.RegisterDevice.Error.Taken'));
      return;
    }

    let createPosDeviceId: string | undefined;
    const createRes = await createPOSDeviceMutation({
      variables: {
        name: nameInput,
        venueId,
        lastSeen: new Date().toISOString(),
        companyId,
      },
    });

    if (createRes.data && createRes.data.createPOSDevice) {
      createPosDeviceId = createRes.data.createPOSDevice.id;
      updateStorage({
        deviceId: createPosDeviceId,
      });
      logger.info('New POS device created successfully', {
        id: createPosDeviceId,
        name: nameInput,
      });
    }

    if (!createPosDeviceId) {
      setError(i18n('Onboarding.RegisterDevice.Error.Failed')); // logger.error is called in the deviceSettings
      return;
    }

    const claimRes = await claimPosDevice({
      posDeviceId: createPosDeviceId,
      posDeviceName: nameInput,
    });

    // If user had never created a device before, we track it
    if (isFirstPosDeviceCreated) {
      onbTracker.event(ONBOARDING_EVENT_STEP.CLAIMED_DEVICE);
    }

    if (!claimRes.success) {
      setError(claimRes?.message || i18n('Onboarding.RegisterDevice.Error.General'));
      return;
    }

    onComplete();
  };

  // Here we claim an existing unclaimed POS Device and create a new Device with the same name & claim them both
  // If user had available POS Devices and selects and claims one
  const selectPosDeviceFlow = async () => {
    const selectedPosDeviceName = posDevices.find((d) => d.id === selectedPosDeviceId)!.name;

    const res = await claimPosDevice({
      posDeviceId: selectedPosDeviceId!,
      posDeviceName: selectedPosDeviceName,
    });

    if (!res.success) {
      setError(res?.message || i18n('Onboarding.RegisterDevice.Error.General'));
      return;
    }

    onComplete();
  };

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    let input = e.target.value;

    if (isSwedishCompany) {
      // Compliance requirements in Sweden
      input = input.toLowerCase();
    }

    setNameInput(input);
  };

  const handleSubmit = async () => {
    setError(null);
    setLoading(true);

    view === VIEW.CREATE ? await createPosDeviceFlow() : await selectPosDeviceFlow();

    setLoading(false);
  };

  const renderHeadingText = () => {
    switch (view) {
      case VIEW.CREATE:
        return i18n('ClaimDevice.RegisterDevice.Create');
      case VIEW.CLAIM:
        return i18n('ClaimDevice.RegisterDevice.Claim');
      default:
        return isChangeOfDevice
          ? i18n('ClaimDevice.ChangeDevice')
          : i18n('ClaimDevice.RegisterDevice');
    }
  };

  const renderSubtitleText = () => {
    switch (view) {
      case VIEW.CREATE:
        return i18n('ClaimDevice.RegisterDevice.CreateText');
      case VIEW.CLAIM:
        return i18n('Onboarding.Select.Select.Desc');
      default:
        return i18n('ClaimDevice.Select.CreateOrSelect');
    }
  };

  const createNewButton = useButton({
    onClick: () => {
      setView(VIEW.CREATE);
    },
  });

  const selectExistingButton = useButton({
    onClick: () => {
      setView(VIEW.CLAIM);
    },
  });

  const submitButton = useButton({
    onClick: () => {
      handleSubmit();
    },
    disabled: loading || view === VIEW.CREATE ? nameInput === '' : !selectedPosDeviceId,
  });

  const backButton = useButton({
    onClick: () => {
      setError(null);
      setNameInput('');
      setSelectedPosDeviceId(null);
      setView(VIEW.SELECT);
    },
  });

  if (posDevicesQuery?.loading && !posDevicesQuery.data) {
    return (
      <LoadingWrapper
        display="flex"
        alignItems="center"
        flexDirection="column"
        gap="small"
        justifyContent="center">
        <Loader color="var(--colorWhite)" />
        <Paragraph level={3} color="var(--colorWhite)">
          {i18n('Onboarding.RegisterDevice.Loading')}
        </Paragraph>
      </LoadingWrapper>
    );
  }

  return (
    <>
      {view !== VIEW.SELECT && (
        <BackButton display="flex" alignItems="center" {...backButton}>
          <BackButtonIcon />
          {i18n('General.GoBack')}
        </BackButton>
      )}

      <HeaderBox
        display="flex"
        flexDirection="column"
        alignItems="center"
        gap="xs"
        justifyContent="center">
        <Icon
          height={isHandy ? '48px' : '64px'}
          width={isHandy ? '48px' : '64px'}
          src={DevicesIcon}
          color="var(--colorBlueGray200)"
        />
        <Heading level={isHandy ? 3 : 1} color="var(--colorWhite)">
          {renderHeadingText()}
        </Heading>
        <Paragraph level={isHandy ? 5 : 2} color="var(--colorBlueGray200)">
          {renderSubtitleText()}
        </Paragraph>
      </HeaderBox>

      {view === VIEW.SELECT ? (
        <Box fluid display="flex" gap={isHandy ? 'small' : 'large'} marginTop="medium">
          <BlueButton fluid {...createNewButton}>
            {i18n('Onboarding.Select.Create')}
          </BlueButton>
          <Button fluid {...selectExistingButton}>
            {i18n('Onboarding.Select.Select')}
          </Button>
        </Box>
      ) : (
        <CreateOrClaimBox
          display="flex"
          flexDirection="column"
          alignItems="center"
          fluid
          justifyContent={isHandy ? 'flex-start' : loading ? 'center' : 'space-between'}>
          {view === VIEW.CREATE ? (
            <Box fluid alignItems="center" display="flex" flexDirection="column" gap="xs">
              <FormatInput
                value={nameInput}
                onChange={handleInputChange}
                type="text"
                placeholder={i18n('ClaimDevice.Create.Placeholder')}
              />
              {isSwedishCompany && (
                <Paragraph level={5} color="var(--colorBlueGray400)">
                  {i18n('Onboarding.RegisterDevice.CreateText.SwedishInfo')}
                </Paragraph>
              )}
            </Box>
          ) : (
            <DevicesBox fluid display="flex" flexDirection="column">
              {posDevices.map((posDevice) => (
                <SelectableDevice
                  key={posDevice.id}
                  posDevice={posDevice}
                  isSelected={posDevice.id === selectedPosDeviceId}
                  onSelect={(id: string) => {
                    if (!loading) {
                      setSelectedPosDeviceId(id);
                    }
                  }}
                />
              ))}
            </DevicesBox>
          )}

          <BlueButton fluid marginTop="medium" {...submitButton}>
            {loading ? (
              <Loader size="sm" color="var(--colorWhite)" />
            ) : view === VIEW.CREATE ? (
              i18n('Onboarding.Create')
            ) : (
              i18n('Onboarding.Claim')
            )}
          </BlueButton>

          {error && (
            <Box padding="small">
              <Paragraph level={4} color="var(--colorUIRed)">
                {error}
              </Paragraph>
            </Box>
          )}
        </CreateOrClaimBox>
      )}
    </>
  );
};

export default ClaimDevice;
